import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
    Drawer, Typography, Flex, Tag, Spin, Button, Select, Input, Popconfirm,
    Space, Tooltip, Row, Col, Popover, AutoComplete, theme, message, Modal,
} from 'antd';
import {
    StarFilled, FilePdfFilled, StopOutlined, UserOutlined,
    PlusOutlined, TeamOutlined, SwapOutlined, CloseCircleOutlined,
    ExclamationCircleOutlined,
} from '@ant-design/icons';
import _ from 'lodash';
import { socket, joinRoom, leaveRoom } from '../../io';
import PhoneDisplay from '../common/PhoneDisplay';
import ThreadList from '../inbox-v2/components/Threads/ThreadList';
import MessageInputField from '../inbox-v2/components/MessageInputField/MessageInputField';
import {
    getContactModel,
    getContactThreads,
    sendReply,
    getUserMessageTemplates,
    getSequenceTemplates,
    addContactTag as apiAddContactTag,
    deleteContactTag as apiDeleteContactTag,
    setContactStar,
    downloadContact,
    getCompanyGroups,
    addContactToGroups,
    removeContactFromGroups,
    createGroup,
} from '../inbox-v2/resources';
import useFlowEnrollmentStore from '../../stores/useFlowEnrollmentStore';

const { Title, Text } = Typography;

/* ------------------------------------------------------------------ */
/*  DrawerTags – prop-driven tag editor (not tied to inbox store)     */
/* ------------------------------------------------------------------ */
const DrawerTags = ({ contactModel, onRefresh }) => {
    const { token } = theme.useToken();
    const [inputVisible, setInputVisible] = useState(false);
    const [inputValue, setInputValue] = useState('');
    const [pendingTag, setPendingTag] = useState(null);
    const inputRef = useRef(null);

    const tags = (contactModel.tags || []).map((t) => ({
        id: t.id,
        removable: !t.tag_id,
        value: t.value,
    }));

    useEffect(() => {
        if (inputVisible) inputRef.current?.focus();
    }, [inputVisible]);

    const handleAdd = async () => {
        if (inputValue && !tags.some((t) => t.value === inputValue)) {
            try {
                await apiAddContactTag(contactModel.id, inputValue);
                onRefresh();
            } catch (err) {
                message.error('Failed to add tag');
            }
        }
        setInputVisible(false);
        setInputValue('');
    };

    const handleRemove = async (tagId) => {
        setPendingTag(null);
        try {
            await apiDeleteContactTag(tagId);
            onRefresh();
        } catch (err) {
            message.error('Failed to remove tag');
        }
    };

    return (
        <Flex gap="4px 0" wrap>
            {tags.map((tag) => {
                const isLong = tag.value.length > 20;
                const tagEl = (
                    <Popconfirm
                        key={tag.id}
                        title={`Remove "${tag.value}"?`}
                        onConfirm={() => handleRemove(tag.id)}
                        onCancel={() => setPendingTag(null)}
                        placement="bottomLeft"
                        okText="Yes"
                        cancelText="No"
                        open={pendingTag === tag.id}
                    >
                        <Tag
                            closable={tag.removable}
                            style={{
                                backgroundColor: '#F8FFFB',
                                color: '#0F5132',
                                fontSize: 12,
                                padding: '0 3px',
                                borderRadius: 4,
                                border: '1px solid #A7E6C5',
                                cursor: 'default',
                                margin: 2,
                                userSelect: 'none',
                            }}
                            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#F0FFF2')}
                            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#F8FFFB')}
                            onClose={(e) => {
                                e.preventDefault();
                                setPendingTag(tag.id);
                            }}
                        >
                            {isLong ? `${tag.value.slice(0, 20)}...` : tag.value}
                        </Tag>
                    </Popconfirm>
                );
                return isLong ? (
                    <Tooltip title={tag.value} key={tag.id}>{tagEl}</Tooltip>
                ) : tagEl;
            })}
            {inputVisible ? (
                <Input
                    ref={inputRef}
                    type="text"
                    size="small"
                    style={{ width: 64, height: 22, marginInlineEnd: 8, verticalAlign: 'top' }}
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onBlur={handleAdd}
                    onPressEnter={handleAdd}
                />
            ) : (
                <Tag
                    style={{ height: 22, background: token.colorBgContainer, borderStyle: 'dashed' }}
                    icon={<PlusOutlined />}
                    onClick={() => setInputVisible(true)}
                >
                    New Tag
                </Tag>
            )}
        </Flex>
    );
};

/* ------------------------------------------------------------------ */
/*  DrawerGroups – prop-driven group editor (not tied to inbox store) */
/* ------------------------------------------------------------------ */
const DrawerGroups = ({ contactModel, onRefresh }) => {
    const { token } = theme.useToken();
    const [inputVisible, setInputVisible] = useState(false);
    const [inputValue, setInputValue] = useState('');
    const [options, setOptions] = useState([]);
    const [pendingGroup, setPendingGroup] = useState(null);
    const [loading, setLoading] = useState(false);
    const [companyGroupsList, setCompanyGroupsList] = useState([]);
    const autoCompleteRef = useRef(null);

    const groups = contactModel.groups || [];

    useEffect(() => {
        if (inputVisible && contactModel.company_id) {
            const fetchGroups = async () => {
                try {
                    const res = await getCompanyGroups(contactModel.company_id, contactModel.id);
                    if (res.statusCode === 200) setCompanyGroupsList(res.data || []);
                } catch (err) {
                    // optional
                }
            };
            fetchGroups();
            setTimeout(() => autoCompleteRef.current?.focus(), 50);
        }
    }, [inputVisible]);

    const handleSearch = (searchText) => {
        setInputValue(searchText);
        const memberIds = groups.map((g) => g.id);
        const filtered = companyGroupsList
            .filter((g) => !memberIds.includes(g.id))
            .filter((g) => g.name.toLowerCase().includes(searchText.toLowerCase()));
        const opts = filtered.map((g) => ({ value: g.id, label: g.name, groupId: g.id }));
        if (searchText.trim() && !companyGroupsList.some((g) => g.name.toLowerCase() === searchText.toLowerCase())) {
            opts.push({ value: `new:${searchText}`, label: `Create "${searchText}"`, isNew: true });
        }
        setOptions(opts);
    };

    const handleSelect = async (val, option) => {
        setLoading(true);
        setInputVisible(false);
        setInputValue('');
        try {
            if (option.isNew) {
                await createGroup(val.replace('new:', ''), [contactModel.id]);
            } else {
                await addContactToGroups(contactModel.id, [val]);
            }
            onRefresh();
        } catch (err) {
            message.error('Failed to add group');
        }
        setLoading(false);
    };

    const handleRemove = async (groupId) => {
        setPendingGroup(null);
        try {
            await removeContactFromGroups(contactModel.id, [groupId]);
            onRefresh();
        } catch (err) {
            message.error('Failed to remove group');
        }
    };

    return (
        <Flex gap="4px 0" wrap align="center">
            {groups.length > 0 && (
                <TeamOutlined style={{ color: '#8c8c8c', fontSize: 12, marginRight: 4 }} />
            )}
            {groups.map((group) => (
                <Popconfirm
                    key={group.id}
                    title={`Remove from "${group.name}"?`}
                    onConfirm={() => handleRemove(group.id)}
                    onCancel={() => setPendingGroup(null)}
                    placement="bottomLeft"
                    okText="Yes"
                    cancelText="No"
                    open={pendingGroup === group.id}
                >
                    <Tag
                        closable
                        style={{
                            backgroundColor: '#F0F5FF',
                            color: '#1D39C4',
                            fontSize: 12,
                            padding: '0 3px',
                            borderRadius: 12,
                            border: '1px solid #ADC6FF',
                            cursor: 'default',
                            margin: 2,
                            userSelect: 'none',
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#E6EDFF')}
                        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#F0F5FF')}
                        onClose={(e) => {
                            e.preventDefault();
                            setPendingGroup(group.id);
                        }}
                    >
                        {group.name}
                    </Tag>
                </Popconfirm>
            ))}
            {loading ? (
                <Spin size="small" />
            ) : inputVisible ? (
                <AutoComplete
                    ref={autoCompleteRef}
                    size="small"
                    style={{ width: 140 }}
                    options={options}
                    onSearch={handleSearch}
                    onSelect={handleSelect}
                    onBlur={() => { setInputVisible(false); setInputValue(''); }}
                    value={inputValue}
                    placeholder="Search or create..."
                />
            ) : (
                <Tag
                    style={{ height: 22, background: token.colorBgContainer, borderStyle: 'dashed', cursor: 'pointer' }}
                    icon={<PlusOutlined />}
                    onClick={() => setInputVisible(true)}
                >
                    Group
                </Tag>
            )}
        </Flex>
    );
};

/* ------------------------------------------------------------------ */
/*  CampaignContactDrawer – main component                           */
/* ------------------------------------------------------------------ */
const CampaignContactDrawer = ({
    open,
    onClose,
    contactId,
    enrollment,
    modulegroupId,
    campaignId,
    modules = [],
    flowSnapshot,
    moduleResponseOverrides,
}) => {
    const [contactModel, setContactModel] = useState(null);
    const [contactLoading, setContactLoading] = useState(false);
    const [threads, setThreads] = useState([]);
    const [pdfLoading, setPdfLoading] = useState(false);
    const [dropReason, setDropReason] = useState('');
    const [moveOpen, setMoveOpen] = useState(false);
    const [dropOpen, setDropOpen] = useState(false);
    const [messageTemplates, setMessageTemplates] = useState([]);
    const [sequenceTemplates, setSequenceTemplates] = useState([]);
    const [pendingMoveModuleId, setPendingMoveModuleId] = useState(null);

    const { moveEnrollment, dropEnrollment } = useFlowEnrollmentStore();
    const prevContactId = useRef(null);

    // -- Fetch / refresh contact model --
    const refreshContact = useCallback(async (cId) => {
        try {
            const res = await getContactModel(cId || contactId);
            setContactModel(res.data || null);
        } catch (err) {
            console.error('Failed to fetch contact:', err);
        }
    }, [contactId]);

    useEffect(() => {
        if (!open || !contactId) return;
        if (prevContactId.current !== contactId) {
            setContactModel(null);
            setThreads([]);
            setDropReason('');
            setMoveOpen(false);
            setDropOpen(false);
            setPendingMoveModuleId(null);
            prevContactId.current = contactId;
        }
        const fetch = async () => {
            setContactLoading(true);
            await refreshContact(contactId);
            setContactLoading(false);
        };
        fetch();
    }, [contactId, open]);

    // -- Fetch templates --
    useEffect(() => {
        if (!open) return;
        const fetch = async () => {
            try {
                const [tmplRes, seqRes] = await Promise.all([
                    getUserMessageTemplates().catch(() => ({ data: [] })),
                    getSequenceTemplates().catch(() => ({ data: { rows: [] } })),
                ]);
                setMessageTemplates(tmplRes.data || []);
                setSequenceTemplates(seqRes.data?.rows || []);
            } catch (e) { /* optional */ }
        };
        fetch();
    }, [open]);

    // -- Socket: join/leave contact + conversation rooms for real-time updates --
    // Prefer enrollment prop; fall back to the first loaded thread's conversation_id
    const conversationId = enrollment?.conversation_id
        || (threads.length > 0 ? threads[0].conversation_id : undefined);
    useEffect(() => {
        if (!open || !contactId) return;
        joinRoom(contactId);
        if (conversationId) joinRoom(conversationId);
        return () => {
            leaveRoom(contactId);
            if (conversationId) leaveRoom(conversationId);
        };
    }, [open, contactId, conversationId]);

    // -- Socket: listen for new outbound & inbound messages --
    useEffect(() => {
        if (!open || !contactId) return;

        const pushThread = (thread) => {
            if (!thread || !thread.id) return;
            // Derive channel_name so ThreadItem shows correct sender. Backend
            // enrichment populates thread.user even for bot-driven outbound on
            // longcode lines, so this mostly preserves what the backend sends.
            const channel_name = thread.direction === 'outbound'
                ? (thread.user_id || thread.user?.id
                    ? { type: 'user', name: thread.user?.name || 'User', id: thread.user_id || thread.user?.id, avatar_url: thread.user?.avatar_url || null }
                    : { type: 'system', name: 'Broadcast' })
                : undefined;
            setThreads((prev) => {
                const existingIndex = prev.findIndex((t) => t.id === thread.id);
                const enriched = { ...thread, channel_name: channel_name || thread.channel_name };
                if (existingIndex !== -1) {
                    // Merge so fields set by an earlier event (e.g. channelDetails
                    // from fetchThreads) survive when a later event omits them.
                    const next = [...prev];
                    next[existingIndex] = { ...next[existingIndex], ...enriched };
                    return next;
                }
                return _.orderBy([...prev, enriched], (t) => new Date(t.created_at).getTime(), ['asc']);
            });
        };

        // Outbound messages (e.g. enrollment move) emit threadUpdate to contactId room
        const handleThreadUpdate = (thread) => {
            if (thread.conversation?.contact_id !== contactId) return;
            pushThread(thread);
        };

        // Inbound messages emit fetchThreads to conversation_id room
        const handleFetchThreads = (thread) => {
            if (thread.conversation_id !== conversationId) return;
            pushThread(thread);
        };

        // Inbound messages also emit receiveMsg to userId room
        const handleReceiveMsg = (data) => {
            if (data.contact?.id !== contactId) return;
            pushThread(data.thread);
        };

        // ADR-027 scheduled-message bubble events — keep parity with the inbox.
        // User-initiated cancel removes the bubble outright; other reasons
        // (reply_received, event_cancelled, etc.) morph to "skipped".
        const handleSchedCancelled = (payload) => {
            if (payload.cancelled_reason === 'user') {
                setThreads((prev) => prev.filter((t) => t.id !== payload.id));
                return;
            }
            setThreads((prev) => prev.map((t) =>
                t.id === payload.id
                    ? { ...t, cancelled_reason: payload.cancelled_reason, skipped: true, should_send: false }
                    : t,
            ));
        };
        const handleSchedSent = (payload) => {
            setThreads((prev) => prev.filter((t) => t.id !== payload.id));
        };
        const handleSchedRescheduled = (payload) => {
            if (!payload.schedule) return;
            setThreads((prev) => prev.map((t) =>
                t.id === payload.id ? { ...t, created_at: payload.schedule } : t,
            ));
        };
        const handleSchedDeleted = (payload) => {
            setThreads((prev) => prev.filter((t) => t.id !== payload.id));
        };
        const handleSchedCreated = (payload) => {
            if (!payload || !payload.id) return;
            const belongsToContact = !payload.conversation
                || payload.conversation.contact_id === contactId;
            if (!belongsToContact) return;
            setThreads((prev) => {
                if (prev.some((t) => t.id === payload.id)) return prev;
                return _.orderBy([...prev, payload], (t) => new Date(t.created_at).getTime(), ['asc']);
            });
        };

        // Tag / group changes from another user — refetch the contact model
        // so DrawerTags and DrawerGroups re-render with the new state. The
        // drawer holds its own contactModel rather than a shared store, so we
        // wire the listener locally here (matches existing thread pattern).
        const handleTagsUpdated = (payload) => {
            if (payload?.contactId !== contactId) return;
            refreshContact(contactId);
        };
        const handleGroupsUpdated = (payload) => {
            if (payload?.contactId !== contactId) return;
            refreshContact(contactId);
        };
        const handleStarUpdated = (payload) => {
            if (payload?.contactId !== contactId) return;
            setContactModel((prev) => (prev ? { ...prev, star: !!payload.star } : prev));
        };

        socket.on('threadUpdate', handleThreadUpdate);
        socket.on('fetchThreads', handleFetchThreads);
        socket.on('receiveMsg', handleReceiveMsg);
        socket.on('scheduled_message:cancelled', handleSchedCancelled);
        socket.on('scheduled_message:sent', handleSchedSent);
        socket.on('scheduled_message:rescheduled', handleSchedRescheduled);
        socket.on('scheduled_message:deleted', handleSchedDeleted);
        socket.on('scheduled_message:created', handleSchedCreated);
        socket.on('contact:tags-updated', handleTagsUpdated);
        socket.on('contact:groups-updated', handleGroupsUpdated);
        socket.on('contact:star-updated', handleStarUpdated);
        return () => {
            socket.off('threadUpdate', handleThreadUpdate);
            socket.off('fetchThreads', handleFetchThreads);
            socket.off('receiveMsg', handleReceiveMsg);
            socket.off('scheduled_message:cancelled', handleSchedCancelled);
            socket.off('scheduled_message:sent', handleSchedSent);
            socket.off('scheduled_message:rescheduled', handleSchedRescheduled);
            socket.off('scheduled_message:deleted', handleSchedDeleted);
            socket.off('scheduled_message:created', handleSchedCreated);
            socket.off('contact:tags-updated', handleTagsUpdated);
            socket.off('contact:groups-updated', handleGroupsUpdated);
            socket.off('contact:star-updated', handleStarUpdated);
        };
    }, [open, contactId, conversationId]);

    // -- Thread fetching (matches inbox store behaviour) --
    const retrieveThreads = useCallback(async (cId, page) => {
        try {
            const res = await getContactThreads(cId, `page=${page}`);
            const newThreads = res.data || [];
            if (page === 1) {
                setThreads(_.orderBy(newThreads, ['created_at'], ['asc']));
            } else {
                setThreads((prev) =>
                    _.orderBy([...prev, ...newThreads], ['created_at'], ['asc'])
                );
            }
            return newThreads.length < 15;
        } catch (err) {
            console.error('Failed to fetch threads:', err);
            return true;
        }
    }, []);

    // Initial thread load (after contact model is ready)
    useEffect(() => {
        if (open && contactId && contactModel) {
            retrieveThreads(contactId, 1);
        }
    }, [contactId, open, contactModel]);

    // -- Send message --
    const handleSendMessage = useCallback(async (body, botmapId, cId, contactMobile, moduleGroup, translation) => {
        const data = {
            body, botmapId, contactId: cId, moduleGroup: moduleGroup || '', sendType: 'reply', to: contactMobile,
        };
        if (translation) data.translation = translation; // ADR-035
        const result = await sendReply(data);
        setTimeout(() => retrieveThreads(cId, 1), 600);
        return result;
    }, [retrieveThreads]);

    // -- Star toggle --
    const handleToggleStar = async () => {
        if (!contactModel) return;
        try {
            await setContactStar(contactModel.id, !contactModel.star);
            setContactModel((prev) => ({ ...prev, star: !prev.star }));
        } catch (err) {
            message.error('Failed to update star');
        }
    };

    // -- PDF download (matches inbox ActiveContactHeader behaviour) --
    const handleDownloadPDF = async () => {
        if (!contactModel) return;
        setPdfLoading(true);
        try {
            const res = await downloadContact(contactModel.id);
            const link = document.createElement('a');
            link.href = `${res.data.url}`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (err) {
            console.error('PDF download error:', err);
            message.error('Failed to download PDF');
        } finally {
            setPdfLoading(false);
        }
    };

    // -- Resolve module message text (snapshot → live module + overrides) --
    const getModuleMessageText = (moduleId) => {
        if (flowSnapshot?.modules) {
            const snap = flowSnapshot.modules.find((m) => m.id === moduleId);
            if (snap) {
                const text = snap.response || '';
                const append = snap.response_append || '';
                return append ? `${text}\n${append}` : text;
            }
        }
        const mod = modules.find((m) => m.id === moduleId);
        if (!mod) return null;
        const overrides = moduleResponseOverrides || {};
        const response = (overrides[moduleId] !== undefined && mod.response_user_modify)
            ? overrides[moduleId]
            : (mod.response || '');
        const append = mod.response_append || '';
        return append ? `${response}\n${append}` : response;
    };

    // -- Enrollment actions --
    const handleMoveEnrollment = async (moduleId) => {
        if (enrollment) {
            await moveEnrollment(enrollment.id, modulegroupId, moduleId, campaignId);
            setMoveOpen(false);
            // Refresh threads so the sent message appears (fallback if socket event is delayed)
            setTimeout(() => retrieveThreads(contactId, 1), 800);
        }
    };

    const handleMoveSelect = (moduleId) => {
        if (moduleId === enrollment?.current_module_id) return;
        setPendingMoveModuleId(moduleId);

        const targetModule = modules.find((m) => m.id === moduleId);
        const targetName = targetModule?.title || 'Unknown';
        const messageText = getModuleMessageText(moduleId);

        Modal.confirm({
            title: `Move to "${targetName}"?`,
            icon: <ExclamationCircleOutlined style={{ color: '#faad14' }} />,
            width: 480,
            content: (
                <div>
                    <p style={{ marginBottom: 8 }}>
                        <strong>The message for this step will be sent to the contact.</strong>{' '}
                        Confirm that you want to move the contact and send the following message.
                    </p>
                    {messageText && (
                        <div style={{
                            background: '#f5f5f5',
                            border: '1px solid #d9d9d9',
                            borderRadius: 6,
                            padding: '8px 12px',
                            maxHeight: 160,
                            overflowY: 'auto',
                            whiteSpace: 'pre-wrap',
                            fontSize: 12,
                            color: '#595959',
                        }}>
                            <span style={{ fontSize: 11, color: '#8c8c8c', display: 'block', marginBottom: 4 }}>
                                Message for this step:
                            </span>
                            {messageText}
                        </div>
                    )}
                </div>
            ),
            okText: 'Move & Send',
            cancelText: 'Cancel',
            onOk: () => {
                handleMoveEnrollment(moduleId);
                setPendingMoveModuleId(null);
            },
            onCancel: () => {
                setPendingMoveModuleId(null);
            },
        });
    };

    const handleDropEnrollment = () => {
        if (enrollment) {
            dropEnrollment(enrollment.id, modulegroupId, campaignId, dropReason);
            setDropReason('');
            setDropOpen(false);
        }
    };

    const handleClose = () => {
        setDropReason('');
        setMoveOpen(false);
        setDropOpen(false);
        setPendingMoveModuleId(null);
        onClose();
    };

    const currentModuleName = enrollment && modules.length > 0
        ? (modules.find((m) => m.id === enrollment.current_module_id)?.title || 'Unknown')
        : null;

    return (
        <Drawer
            title={null}
            open={open}
            onClose={handleClose}
            width={520}
            placement="right"
            styles={{
                body: { padding: 0, display: 'flex', flexDirection: 'column', height: '100%' },
            }}
        >
            {contactLoading && !contactModel ? (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                    <Spin />
                </div>
            ) : contactModel ? (
                <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                    {/* ---- Contact Header ---- */}
                    <div style={{ padding: '12px 16px 8px', borderBottom: '1px solid #f0f0f0' }}>
                        {/* Row 1: Name + Action Buttons */}
                        <Row justify="space-between" align="middle" wrap={false}>
                            <Col style={{ minWidth: 0, flex: 1 }}>
                                <Title
                                    level={4}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 6,
                                        margin: 0,
                                        lineHeight: 1.3,
                                        cursor: 'pointer',
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                    }}
                                    onClick={() => window.open(`/contacts/${contactModel.id}`, '_blank')}
                                >
                                    {contactModel.blacklist && (
                                        <Tooltip title="Opted out" placement="top">
                                            <StopOutlined style={{ color: '#ff4d4f', fontSize: 12, flexShrink: 0 }} />
                                        </Tooltip>
                                    )}
                                    {contactModel.name}
                                </Title>
                            </Col>
                            <Col>
                                <Space size={4}>
                                    <Tooltip title={contactModel.star ? 'Remove Star' : 'Star'} placement="bottom">
                                        <Button
                                            onClick={handleToggleStar}
                                            style={{ color: contactModel.star ? 'gold' : 'gray' }}
                                            icon={<StarFilled />}
                                        />
                                    </Tooltip>
                                    <Tooltip title="Download PDF" placement="bottom">
                                        <Button
                                            style={{ color: 'gray' }}
                                            icon={<FilePdfFilled />}
                                            loading={pdfLoading}
                                            onClick={handleDownloadPDF}
                                        />
                                    </Tooltip>
                                    {enrollment && enrollment.status === 'active' && (
                                        <>
                                            <Popover
                                                title="Move to Module"
                                                trigger="click"
                                                open={moveOpen}
                                                onOpenChange={setMoveOpen}
                                                content={
                                                    <div style={{ width: 200 }}>
                                                        <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 6 }}>
                                                            Current: <strong>{currentModuleName}</strong>
                                                        </Text>
                                                        <Select
                                                            style={{ width: '100%' }}
                                                            placeholder="Select module..."
                                                            value={enrollment.current_module_id}
                                                            onChange={handleMoveSelect}
                                                        >
                                                            {modules.map((m) => (
                                                                <Select.Option key={m.id} value={m.id}>
                                                                    {m.title}
                                                                </Select.Option>
                                                            ))}
                                                        </Select>
                                                    </div>
                                                }
                                            >
                                                <Tooltip title="Move Flow Step" placement="bottom" open={moveOpen ? false : undefined}>
                                                    <Button icon={<SwapOutlined />} style={{ color: 'gray' }} />
                                                </Tooltip>
                                            </Popover>
                                            <Popover
                                                title="Drop Enrollment"
                                                trigger="click"
                                                open={dropOpen}
                                                onOpenChange={setDropOpen}
                                                content={
                                                    <div style={{ width: 240 }}>
                                                        <Input.TextArea
                                                            rows={2}
                                                            placeholder="Drop reason (optional)"
                                                            value={dropReason}
                                                            onChange={(e) => setDropReason(e.target.value)}
                                                            style={{ marginBottom: 8 }}
                                                        />
                                                        <Popconfirm
                                                            title="Drop from flow?"
                                                            description={
                                                                <div style={{ maxWidth: 220 }}>
                                                                    <p style={{ margin: '4px 0' }}>
                                                                        <strong>{contactModel?.name || 'This contact'}</strong> will be
                                                                        removed from the flow and will no longer receive automated messages.
                                                                    </p>
                                                                    {currentModuleName && (
                                                                        <p style={{ margin: '4px 0', fontSize: 12, color: '#8c8c8c' }}>
                                                                            Currently on: <strong>{currentModuleName}</strong>
                                                                        </p>
                                                                    )}
                                                                </div>
                                                            }
                                                            onConfirm={handleDropEnrollment}
                                                            okText="Drop"
                                                            okType="danger"
                                                            cancelText="Cancel"
                                                        >
                                                            <Button danger size="small">Confirm Drop</Button>
                                                        </Popconfirm>
                                                    </div>
                                                }
                                            >
                                                <Tooltip title="Drop from Flow" placement="bottom" open={dropOpen ? false : undefined}>
                                                    <Button icon={<CloseCircleOutlined />} style={{ color: 'gray' }} />
                                                </Tooltip>
                                            </Popover>
                                        </>
                                    )}
                                </Space>
                            </Col>
                        </Row>

                        {/* Row 2: Phone + Tags */}
                        <Flex align="center" gap={8} wrap style={{ marginTop: 4 }}>
                            {contactModel.mobile && <PhoneDisplay value={contactModel.mobile} />}
                            <DrawerTags
                                contactModel={contactModel}
                                onRefresh={() => refreshContact(contactModel.id)}
                            />
                        </Flex>

                        {/* Row 3: Groups */}
                        <div style={{ marginTop: 8 }}>
                            <DrawerGroups
                                contactModel={contactModel}
                                onRefresh={() => refreshContact(contactModel.id)}
                            />
                        </div>
                    </div>

                    {/* ---- Enrollment status indicators ---- */}
                    {enrollment && enrollment.status === 'dropped' && (
                        <div style={{ padding: '8px 16px', borderBottom: '1px solid #f0f0f0', background: '#fff2f0' }}>
                            <Tag color="red" style={{ marginBottom: enrollment.drop_reason ? 4 : 0 }}>Dropped</Tag>
                            {enrollment.drop_reason && (
                                <Text type="secondary" style={{ fontSize: 12, fontStyle: 'italic', display: 'block' }}>
                                    {enrollment.drop_reason}
                                </Text>
                            )}
                        </div>
                    )}

                    {enrollment && enrollment.status === 'active' && currentModuleName && (
                        <div style={{ padding: '6px 16px', borderBottom: '1px solid #f0f0f0', background: '#f6ffed' }}>
                            <Text style={{ fontSize: 12 }}>
                                Flow Step: <strong>{currentModuleName}</strong>
                            </Text>
                        </div>
                    )}

                    {/* ---- Thread List ---- */}
                    <div className="campaign-drawer-threads" style={{ flex: 1, minHeight: 0, overflow: 'hidden', background: '#fafafa' }}>
                        <style>{`.campaign-drawer-threads > .scrollableListDiv { height: 100%; }`}</style>
                        <ThreadList
                            contactId={contactModel.id}
                            threads={threads}
                            retrieveThreads={retrieveThreads}
                            contact={contactModel}
                        />
                    </div>

                    {/* ---- Message Input ---- */}
                    <div style={{ borderTop: '1px solid #f0f0f0', padding: '8px 12px' }}>
                        <MessageInputField
                            activeContactModel={contactModel}
                            sendMessage={handleSendMessage}
                            userMessageTemplates={messageTemplates}
                            userSequenceTemplates={sequenceTemplates}
                        />
                    </div>
                </div>
            ) : (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                    <Text type="secondary">Select a contact to view details</Text>
                </div>
            )}
        </Drawer>
    );
};

export default CampaignContactDrawer;
